import { StatusCodes } from "http-status-codes";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorCode, getErrorStatusCode } from "../../../../api/api-response";
import { RestEndpoints, replaceUriParameter } from "../../../../api/endpoints";
import getInstance from "../../../../utils/rest";
import { GroupConverter } from "../../../domain/converter/group.converter";
import { IGroup } from "../../../domain/group/group";
import { IUpdateGroupBaseDataModel } from "../../../models/group/group-update.model";
import { IGroupModel } from "../../../models/group/group.model";
import { SharedQueryKeys } from "../shared-query-keys";

const updateGroupBaseData = async (id: number, updateData: IUpdateGroupBaseDataModel): Promise<IGroup> => {
  const url = replaceUriParameter(RestEndpoints.UpdateGroupBaseData, "id", id);
  const { data } = await getInstance().put<IGroupModel>(url, updateData);
  return GroupConverter.toDomain(data);
};

export const useUpdateGroupBaseData = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updateData }: { id: number; updateData: IUpdateGroupBaseDataModel }) => updateGroupBaseData(id, updateData),

    onSuccess: () => {
      enqueueSnackbar(t("general.successfull_saved"), { variant: "success" });
      queryClient.invalidateQueries({ queryKey: [SharedQueryKeys.GetGroupAddressesForUuid] });
      queryClient.invalidateQueries({ queryKey: [SharedQueryKeys.GetGroupUsers] });
    },
    onError: (error) => {
      const errorMsg = getErrorCode(error);
      const errorCode = getErrorStatusCode(error);
      if (errorCode !== StatusCodes.INTERNAL_SERVER_ERROR) return;
      enqueueSnackbar(t("general.error_occurred", { errorCode, errorMsg }), { variant: "error" });
    },
  });
};
